import { useCallback } from 'react';

// ==========================================
// useMoneyPosInput
// ==========================================
// Patron de entrada numerica tipo calculadora bancaria / terminal POS:
//   - El campo siempre se muestra en formato 0.00
//   - Los digitos entran por la derecha (centavos primero) y todo se
//     recorre a la izquierda: 1,2,5 -> 0.01 -> 0.12 -> 1.25
//   - Backspace quita el ultimo digito capturado (recorre a la derecha).
//     En 0.00 no hace nada (floor(0/10)=0 es un no-op natural)
//   - Delete limpia el importe completo a 0.00 de un solo golpe
//   - No existe "posicion de cursor" -- cualquier tecla de navegacion
//     (flechas, Home/End) se ignora; el cursor se manda siempre al
//     final en focus/click/seleccion (ver handleSelect)
//   - Internamente opera en centavos enteros para evitar drift de
//     punto flotante; onUpdate/onCommit reciben el valor en pesos
//
// Callbacks:
//   onUpdate(val) -> cada tecla -> actualiza display en detailData
//   onCommit(val) -> onBlur    -> valor final confirmado
// ==========================================

const MAX_CENTS = 999999999999; // hasta $9,999,999,999.99

const useMoneyPosInput = (value, onUpdate, onCommit) => {

  const valueToCents = useCallback((val) => Math.round((parseFloat(val) || 0) * 100), []);
  const centsToStr = useCallback((cents) => (cents / 100).toFixed(2), []);

  const forceCursorToEnd = useCallback((input) => {
    const len = input.value.length;
    if (input.selectionStart !== len || input.selectionEnd !== len) {
      input.setSelectionRange(len, len);
    }
  }, []);

  const handleFocus = useCallback((e) => {
    e.target.value = centsToStr(valueToCents(value));
    requestAnimationFrame(() => {
      if (e.target === document.activeElement) forceCursorToEnd(e.target);
    });
  }, [value, valueToCents, centsToStr, forceCursorToEnd]);

  const handleSelect = useCallback((e) => {
    forceCursorToEnd(e.target);
  }, [forceCursorToEnd]);

  const handleKeyDown = useCallback((e) => {
    if (['Tab', 'Enter'].includes(e.key)) return;
    if (e.ctrlKey || e.metaKey) return;
    if (e.key === 'Escape') return;

    e.preventDefault();

    const input = e.target;
    let cents = valueToCents(input.value);

    if (/^[0-9]$/.test(e.key)) {
      const nextCents = cents * 10 + Number(e.key);
      if (nextCents > MAX_CENTS) return;
      cents = nextCents;
    } else if (e.key === 'Backspace') {
      cents = Math.floor(cents / 10);
    } else if (e.key === 'Delete') {
      cents = 0;
    } else {
      // Flechas, Home/End, etc.: no hay posicion de cursor que mover
      return;
    }

    const newVal = centsToStr(cents);
    input.value = newVal;
    forceCursorToEnd(input);
    onUpdate(cents / 100);
  }, [onUpdate, valueToCents, centsToStr, forceCursorToEnd]);

  const handleBlur = useCallback((e) => {
    const val = valueToCents(e.target.value) / 100;
    onCommit?.(val);
  }, [onCommit, valueToCents]);

  return {
    handleFocus,
    handleKeyDown,
    handleBlur,
    handleSelect
  };
};

export default useMoneyPosInput;
